import React, {useMemo} from 'react'
import {DataObjectId} from 'yougile-model'
import {Item} from '@api/model/item'
import {UserObj} from '@api/model/user-obj'
import {DepartmentsClient} from '@client/departments-client'
import {EmployeeSettings} from '@client/employee-settings'
import {UsersTable} from '@components/common/base/users-table'
import {UsersTable as CompactUsersTable} from '@components/common/users-table/users-table'
import {TableUserInfo} from '@components/common/users-table/users-table'
import {cn} from '@containers/html-utils'

export interface UserTablesProps {
  users: UserObj[]
  selected: DataObjectId[] | DataObjectId | undefined
  onChange: (selected: DataObjectId[] | DataObjectId | undefined) => void
  /**
   * If true, multiple users can be selected. In this case, the onSelectedChanged function will return an array of selected users and the selected prop will be an array.
   */
  multiple: boolean
  /**
   * If true, checkboxes will be displayed before each user instead of check icons.
   */
  useCheckbox?: boolean
  /**
   * Project for which the users are displayed. If specified, the role of each user in the project will be displayed.
   */
  project?: Item
  /**
   * Size of the table.
   * compact - small table with small text, and compact paddings.
   * medium - big table with wide paddings and medium text.
   * large - same as medium, but with large text.
   */
  size?: 'compact' | 'medium' | 'large'
  /**
   * Function that will be called when the user select a user.
   * @param ids
   */
  onToggled?: (ids: DataObjectId[]) => void
  className?: string
}

export const SharedUsersPicker = (props: UserTablesProps) => {
  const {
    users,
    selected,
    onChange,
    multiple,
    useCheckbox,
    project,
    size = 'compact',
    className
  } = props
  const usersTableInfos: TableUserInfo[] = useMemo(() => {
    return users.map(userObj => {
      return {
        id: userObj.id,
        userId: userObj.id,
        userName: userObj.name,
        position: EmployeeSettings.getPosition(userObj.id),
        departments: DepartmentsClient.listForUser(userObj.box, userObj.id)
          .map(x => x.title)
          .join(', '),
        email: userObj.email,
        avatar: userObj.avatar,
        role: project ? userObj.getRole(project) : ''
      }
    })
  }, [])

  const usersTableSelected = useMemo(() => {
    if (selected) {
      return Array.isArray(selected) ? selected : [selected]
    }
    return []
  }, [selected])

  const handleSelectedChanged = (selected: DataObjectId[]) => {
    if (multiple) {
      onChange(selected)
    } else {
      onChange(selected && selected.length > 0 ? selected[0] : undefined)
    }
  }

  /**
   * Notice about h-full className:
   * Without this class, the component scrollbar will not work, because second children div will not fit the parent div, therefore the scrollbar will not appear.
   * It is a bug of UsersTable component. It should be fixed in the future. But for now, we need to add h-full class to make it work.
   */
  return size === 'compact' ? (
    <CompactUsersTable
      users={usersTableInfos}
      selected={usersTableSelected}
      singleMode={!multiple}
      useCheckbox={useCheckbox}
      onSelectedChanged={handleSelectedChanged}
      onToggled={props.onToggled}
      className={cn('h-full', className)}
    />
  ) : (
    <UsersTable
      users={usersTableInfos}
      selected={usersTableSelected}
      singleMode={!multiple}
      useCheckbox={useCheckbox}
      onSelectedChanged={handleSelectedChanged}
      onToggled={props.onToggled}
      className={cn('h-full', className)}
      large={size === 'large'}
    />
  )
}
